import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import "./ActiveFiresTimeline.css";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import FastRewindRoundedIcon from "@mui/icons-material/FastRewindRounded";
import FastForwardRoundedIcon from "@mui/icons-material/FastForwardRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { useTranslation } from "react-i18next";
import {
  formatStamp,
  formatWeekday,
  isSameDay,
} from "../../../utils/activeFiresFormat";

const SPEEDS = [1, 2, 4, 8];
// Duration of one step at 1x. Higher speeds divide it.
const BASE_STEP_MS = 600;
// A day label is only drawn if its day covers at least this share of the chart.
const MIN_DAY_LABEL_SHARE = 0.07;
// Zoom limits / behaviour.
const MIN_VIEW = 6; // never show fewer than this many buckets
const HOUR_LABELS_MAX_VIEW = 48; // below this window size, label hours not days
const HOUR_LABEL_TARGET = 6; // roughly how many hour labels to draw

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
const pad2 = (v) => String(v).padStart(2, "0");

const ActiveFiresTimeline = ({
  cursor,
  onCursorChange,
  buckets,
  onToggleLayer,
  isLayerActive,
  cumulative = false,
  onCumulativeChange,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const rootRef = useRef(null);
  const chartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const prevBucketsRef = useRef(buckets);
  const cursorRef = useRef(0);
  const viewRef = useRef({ start: 0, end: 0 });
  const wheelHandlerRef = useRef(null);
  const onCursorChangeRef = useRef(onCursorChange);
  onCursorChangeRef.current = onCursorChange;

  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(true);
  const [hoverIndex, setHoverIndex] = useState(null);
  // Visible window over the buckets, in GLOBAL indices. `null` = show all.
  const [view, setView] = useState(null);

  const n = buckets ? buckets.length : 0;
  const hasData = n > 0;
  const isTimelineDisabled = n <= 1;

  // Defensive clamp: `cursor` is owned by the parent and may lag behind a
  // freshly swapped `buckets` array for a frame.
  const safeCursor = clamp(cursor ?? 0, 0, Math.max(n - 1, 0));
  cursorRef.current = safeCursor;

  // Visible window (clamped defensively, same reason as the cursor).
  const lastIndex = Math.max(n - 1, 0);
  const viewStart = view ? clamp(view.start, 0, lastIndex) : 0;
  const viewEnd = view ? clamp(view.end, viewStart, lastIndex) : lastIndex;
  const viewLen = viewEnd - viewStart + 1;
  const isZoomed = n > 0 && viewLen < n;
  viewRef.current = { start: viewStart, end: viewEnd };

  const visible = useMemo(
    () => (buckets || []).slice(viewStart, viewEnd + 1),
    [buckets, viewStart, viewEnd],
  );
  // Counts of the visible window only: bars rescale to the window, so small
  // peaks become readable when zoomed in.
  const counts = useMemo(
    () => visible.map((b) => b.features?.length ?? 0),
    [visible],
  );
  const maxCount = useMemo(
    () => counts.reduce((m, c) => (c > m ? c : m), 0),
    [counts],
  );
  // Peak inside the visible window, as a global index.
  const peakIndex = useMemo(
    () => viewStart + Math.max(counts.indexOf(maxCount), 0),
    [counts, maxCount, viewStart],
  );

  // Axis labels. Zoomed in far enough -> hour labels, otherwise one label per
  // day, centred under the span of buckets belonging to that day.
  const hourMode = isZoomed && viewLen <= HOUR_LABELS_MAX_VIEW;
  const dayLabels = useMemo(() => {
    if (visible.length === 0) return [];
    const total = visible.length;

    if (hourMode) {
      const step = Math.max(1, Math.ceil(total / HOUR_LABEL_TARGET));
      const labels = [];
      for (let i = Math.floor(step / 2); i < total; i += step) {
        const d = new Date(visible[i].timestamp);
        labels.push({
          key: i,
          left: ((i + 0.5) / total) * 100,
          label: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
        });
      }
      return labels;
    }

    const spans = [];
    visible.forEach((b, i) => {
      const last = spans[spans.length - 1];
      if (last && isSameDay(last.date, b.timestamp)) last.end = i;
      else spans.push({ date: b.timestamp, start: i, end: i });
    });
    return spans
      .map((s) => ({
        key: s.start,
        share: (s.end - s.start + 1) / total,
        left: ((s.start + s.end + 1) / 2 / total) * 100,
        label: formatWeekday(s.date, lang),
      }))
      .filter((s) => s.share >= MIN_DAY_LABEL_SHARE);
  }, [visible, hourMode, lang]);

  const stopPlayback = useCallback(() => setIsPlaying(false), []);

  // New dataset (new preset / interval) -> stop any running playback so a
  // stale interval never advances the cursor against the old buckets, and
  // reset the zoom window.
  useEffect(() => {
    if (prevBucketsRef.current !== buckets) {
      stopPlayback();
      setHoverIndex(null);
      setView(null);
      prevBucketsRef.current = buckets;
    }
  }, [buckets, stopPlayback]);

  // Layer hidden -> stop playback.
  useEffect(() => {
    if (!isLayerActive && isPlaying) stopPlayback();
  }, [isLayerActive, isPlaying, stopPlayback]);

  // Dataset shrank to a single point -> nothing to play.
  useEffect(() => {
    if (isTimelineDisabled && isPlaying) stopPlayback();
  }, [isTimelineDisabled, isPlaying, stopPlayback]);

  // Playback loop: runs inside the visible window only.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(() => {
      const { start, end } = viewRef.current;
      const current = cursorRef.current;
      if (current < start) {
        onCursorChangeRef.current(start);
      } else if (current >= end) {
        if (loop) onCursorChangeRef.current(start);
        else setIsPlaying(false);
      } else {
        onCursorChangeRef.current(current + 1);
      }
    }, BASE_STEP_MS / speed);
    return () => clearInterval(id);
  }, [isPlaying, speed, loop, n]);

  /* ---------- zoom / pan ---------- */
  // Applies a new window and keeps the cursor inside it.
  const applyView = (start, end) => {
    const s = clamp(start, 0, lastIndex);
    const e = clamp(end, s, lastIndex);
    setView(s <= 0 && e >= lastIndex ? null : { start: s, end: e });
    setHoverIndex(null);
    const c = cursorRef.current;
    if (c < s || c > e) onCursorChangeRef.current(clamp(c, s, e));
  };

  // Resize the window by `factor` around a GLOBAL index `center`.
  const zoomTo = (center, factor) => {
    if (isTimelineDisabled) return;
    const newLen = clamp(Math.round(viewLen * factor), Math.min(MIN_VIEW, n), n);
    const start = clamp(Math.round(center - newLen / 2), 0, n - newLen);
    applyView(start, start + newLen - 1);
  };

  const zoomIn = () => zoomTo(safeCursor, 0.5);
  const zoomOut = () => zoomTo(safeCursor, 2);
  const resetZoom = () => applyView(0, lastIndex);

  const panBy = (delta) => {
    if (!isZoomed) return;
    const start = clamp(viewStart + delta, 0, n - viewLen);
    applyView(start, start + viewLen - 1);
  };

  // Ctrl/Cmd + wheel = zoom around the pointer; Shift + wheel (or horizontal
  // wheel) = pan. Needs a non-passive listener to be able to preventDefault.
  wheelHandlerRef.current = (e) => {
    const el = chartRef.current;
    if (!el || isTimelineDisabled) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      zoomTo(viewStart + ratio * viewLen, e.deltaY < 0 ? 0.7 : 1.4);
    } else if (
      isZoomed &&
      (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY))
    ) {
      e.preventDefault();
      const delta = e.deltaX || e.deltaY;
      const step = Math.max(1, Math.round(viewLen / 10));
      panBy(delta > 0 ? step : -step);
    }
  };

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return undefined;
    const handler = (e) => wheelHandlerRef.current?.(e);
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [hasData]);

  /* ---------- transport ---------- */
  // Navigation is confined to the visible window.
  const goTo = (index) => {
    if (isTimelineDisabled) return;
    stopPlayback();
    onCursorChange(clamp(index, viewStart, viewEnd));
  };

  const togglePlay = () => {
    if (isTimelineDisabled) return;
    if (isPlaying) {
      stopPlayback();
      return;
    }
    // Data was hidden via the eye icon: replaying brings it back.
    if (!isLayerActive) onToggleLayer?.();
    if (!loop && safeCursor >= viewEnd) onCursorChange(viewStart);
    setIsPlaying(true);
  };

  /* ---------- chart scrubbing ---------- */
  // Returns a GLOBAL bucket index.
  const indexFromEvent = (e) => {
    const el = chartRef.current;
    if (!el || n === 0) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    return viewStart + Math.min(viewLen - 1, Math.floor(ratio * viewLen));
  };

  const handlePointerDown = (e) => {
    if (isTimelineDisabled) return;
    isDraggingRef.current = true;
    stopPlayback();
    const index = indexFromEvent(e);
    setHoverIndex(index);
    onCursorChange(index);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const index = indexFromEvent(e);
    setHoverIndex(index);
    if (isDraggingRef.current) onCursorChange(index);
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
  };

  const handlePointerLeave = () => {
    if (!isDraggingRef.current) setHoverIndex(null);
  };

  /* ---------- keyboard: Space = play, arrows = step, +/- = zoom ---------- */
  const handleKeyDown = (e) => {
    if (e.target.closest("button, input, a")) return;
    if (e.code === "Space") {
      e.preventDefault();
      togglePlay();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(safeCursor - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(safeCursor + 1);
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomIn();
    } else if (e.key === "-") {
      e.preventDefault();
      zoomOut();
    } else if (e.key === "0") {
      e.preventDefault();
      resetZoom();
    }
  };

  if (n === 0) return null;

  const fireLabel = (count) =>
    t("activeFires.timeline.fireCount", {
      count,
      defaultValue_one: "{{count}} fire",
      defaultValue_other: "{{count}} fires",
    });

  const cursorTimestamp = buckets[safeCursor]?.timestamp;
  const tipIndex =
    hoverIndex === null ? null : clamp(hoverIndex, viewStart, viewEnd);
  const tipTimestamp = tipIndex === null ? null : buckets[tipIndex]?.timestamp;
  const tipPct =
    tipIndex === null
      ? 0
      : clamp(((tipIndex - viewStart + 0.5) / viewLen) * 100, 20, 80);

  const zoomInLabel = t("activeFires.timeline.zoomIn", "Zoom in");
  const zoomOutLabel = t("activeFires.timeline.zoomOut", "Zoom out");
  const resetZoomLabel = t("activeFires.timeline.resetZoom", "Reset zoom");

  return (
    <>
      <div
        className="afp-timeline"
        ref={rootRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        data-testid="afp-timeline"
      >
        <div className="afp-timeline-top">
          <span className="afp-label">
            {t("activeFires.timeline.replay", "Replay")}
          </span>
          <span className="afp-timeline-time" data-testid="afp-timeline-time">
            {cursorTimestamp && formatStamp(cursorTimestamp, lang)}
          </span>
        </div>

        <div
          className="afp-chart"
          ref={chartRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          data-testid="afp-chart"
        >
          {tipTimestamp && (
            <div className="afp-chart-tip" style={{ left: `${tipPct}%` }}>
              {formatStamp(tipTimestamp, lang)} ·{" "}
              {fireLabel(counts[tipIndex - viewStart] ?? 0)}
            </div>
          )}
          <div className="afp-bars">
            {counts.map((count, i) => {
              const globalI = viewStart + i;
              const classes = ["afp-bar"];
              if (count === 0) classes.push("is-zero");
              if (globalI < safeCursor) classes.push("is-past");
              if (globalI === safeCursor) classes.push("is-current");
              if (globalI === tipIndex) classes.push("is-hover");
              return (
                <div key={globalI} className="afp-cell">
                  <div
                    className={classes.join(" ")}
                    style={
                      count === 0
                        ? undefined
                        : {
                            height: `${Math.max(10, (count / maxCount) * 100)}%`,
                          }
                    }
                  />
                  {globalI === safeCursor && (
                    <div className="afp-chart-cursor">
                      <span className="afp-chart-knob" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="afp-days">
          {dayLabels.map((d) => (
            <span key={d.key} style={{ left: `${d.left}%` }}>
              {d.label}
            </span>
          ))}
        </div>

        <div className="afp-controls">
          <div className="afp-transport">
            <button
              type="button"
              className="afp-icon-btn"
              onClick={() => goTo(viewStart)}
              disabled={isTimelineDisabled}
              aria-label={t("activeFires.timeline.toStart", "Go to start")}
              title={t("activeFires.timeline.toStart", "Go to start")}
            >
              <SkipPreviousRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="afp-icon-btn is-step"
              onClick={() => goTo(safeCursor - 1)}
              disabled={isTimelineDisabled}
              aria-label={t("activeFires.timeline.stepBack", "Step back")}
              title={t("activeFires.timeline.stepBack", "Step back")}
            >
              <FastRewindRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="afp-play"
              onClick={togglePlay}
              disabled={isTimelineDisabled}
              aria-label={
                isPlaying
                  ? t("activeFires.timeline.pause", "Pause")
                  : t("activeFires.timeline.play", "Play")
              }
              data-testid="afp-timeline-play-pause-button"
            >
              {isPlaying ? (
                <PauseRoundedIcon data-testid="afp-timeline-pause-icon" />
              ) : (
                <PlayArrowRoundedIcon data-testid="afp-timeline-play-icon" />
              )}
            </button>
            <button
              type="button"
              className="afp-icon-btn is-step"
              onClick={() => goTo(safeCursor + 1)}
              disabled={isTimelineDisabled}
              aria-label={t("activeFires.timeline.stepForward", "Step forward")}
              title={t("activeFires.timeline.stepForward", "Step forward")}
            >
              <FastForwardRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="afp-icon-btn"
              onClick={() => goTo(viewEnd)}
              disabled={isTimelineDisabled}
              aria-label={t("activeFires.timeline.toEnd", "Go to end")}
              title={t("activeFires.timeline.toEnd", "Go to end")}
            >
              <SkipNextRoundedIcon fontSize="small" />
            </button>
          </div>

          <div
            className="afp-zoom-group"
            role="group"
            aria-label={t("activeFires.timeline.zoom", "Zoom")}
            data-testid="afp-timeline-zoom"
          >
            <button
              type="button"
              className="afp-icon-btn"
              onClick={zoomOut}
              disabled={isTimelineDisabled || !isZoomed}
              aria-label={zoomOutLabel}
              title={zoomOutLabel}
              data-testid="afp-zoom-out"
            >
              <ZoomOutRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="afp-icon-btn"
              onClick={zoomIn}
              disabled={isTimelineDisabled || viewLen <= Math.min(MIN_VIEW, n)}
              aria-label={zoomInLabel}
              title={zoomInLabel}
              data-testid="afp-zoom-in"
            >
              <ZoomInRoundedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="afp-icon-btn"
              onClick={resetZoom}
              disabled={!isZoomed}
              aria-label={resetZoomLabel}
              title={resetZoomLabel}
              data-testid="afp-zoom-reset"
            >
              <RestartAltRoundedIcon fontSize="small" />
            </button>
          </div>

          <div
            className="afp-speed"
            role="group"
            aria-label={t("activeFires.timeline.speed", "Speed")}
          >
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                className={speed === s ? "active" : ""}
                aria-pressed={speed === s}
                onClick={() => setSpeed(s)}
              >
                {s}×
              </button>
            ))}
          </div>
        </div>

        <div className="afp-chips">
          <button
            type="button"
            className="afp-chip"
            onClick={() => goTo(peakIndex)}
            disabled={isTimelineDisabled || maxCount === 0}
          >
            {t("activeFires.timeline.jumpToPeak", "Jump to peak")}
          </button>
          <button
            type="button"
            className={`afp-chip ${loop ? "on" : ""}`}
            aria-pressed={loop}
            onClick={() => setLoop((v) => !v)}
          >
            {t("activeFires.timeline.loop", "Loop")}
          </button>
          <button
            type="button"
            className={`afp-chip ${cumulative ? "on" : ""}`}
            aria-pressed={cumulative}
            onClick={() => onCumulativeChange?.(!cumulative)}
          >
            {t("activeFires.timeline.cumulative", "Cumulative")}
          </button>
        </div>
      </div>
    </>
  );
};

export default React.memo(ActiveFiresTimeline);